// Monaco Editor SQL Autocomplete Test Utilities

interface MonacoEditor {
  setValue: (value: string) => void;
  setPosition: (position: { lineNumber: number; column: number }) => void;
  trigger: (source: string, handlerId: string, payload?: unknown) => void;
}

// Extend window type for Monaco testing functions  
declare global {
  interface Window {
    testMonacoSQL?: (editor?: MonacoEditor) => void;
    enableMonacoDebug?: () => void;
    disableMonacoDebug?: () => void;
  }
}

export const testAutocompleteSuggestions = (
  editor: MonacoEditor,
  testCases: {
    sql: string;
    cursorPosition: { lineNumber: number; column: number };
    expectedSuggestionTypes: string[];
    description: string;
  }[]
) => {
  console.log('=== Monaco SQL Autocomplete Test ===');
  
  for (const [index, testCase] of testCases.entries()) {
    console.log(`\nTest ${index + 1}: ${testCase.description}`);
    console.log('SQL:', testCase.sql);
    
    // Set the SQL content
    editor.setValue(testCase.sql);
    
    // Set cursor position
    editor.setPosition(testCase.cursorPosition);
    
    // Try to trigger autocomplete
    editor.trigger('test', 'editor.action.triggerSuggest', {});
    
    console.log(`Cursor at line ${testCase.cursorPosition.lineNumber}, column ${testCase.cursorPosition.column}`);
    console.log('Expected:', testCase.expectedSuggestionTypes);
  }
  
  console.log('\n=== Test Complete ===');
  console.log('Check the Monaco suggestion widget for actual results.');
  console.log('Enable debug mode with: localStorage.setItem("monaco-debug", "true")');
};

export const getCommonTestCases = () => [
  {
    sql: 'SELECT * FROM users WHERE ',
    cursorPosition: { lineNumber: 1, column: 26 },
    expectedSuggestionTypes: ['columns', 'keywords'],
    description: 'Basic WHERE clause - should show columns'
  },
  {
    sql: 'SELECT * FROM users WHERE desc',
    cursorPosition: { lineNumber: 1, column: 30 },
    expectedSuggestionTypes: ['columns starting with "desc"'],
    description: 'Partial column name in WHERE - should show matching columns'
  },
  {
    sql: 'SELECT * FROM users WHERE id = 1 AND ',
    cursorPosition: { lineNumber: 1, column: 37 },
    expectedSuggestionTypes: ['columns', 'keywords'],
    description: 'After AND in WHERE - should show columns'
  },
  {
    sql: 'SELECT name, ',
    cursorPosition: { lineNumber: 1, column: 13 },
    expectedSuggestionTypes: ['columns', 'functions'],
    description: 'After comma in SELECT - should show columns'
  },
  {
    sql: 'SELECT * FROM ',
    cursorPosition: { lineNumber: 1, column: 14 },
    expectedSuggestionTypes: ['tables'],
    description: 'After FROM - should show tables'
  }
];

// Global test function that can be called from browser console
window.testMonacoSQL = (editor?: MonacoEditor) => {
  const testCases = getCommonTestCases();

  if (!editor) {
    console.error('No editor provided. Get editor reference first.');
    console.log('Usage: testMonacoSQL(editorInstance)');
    return;
  }

  testAutocompleteSuggestions(editor, testCases);
};

// Helper to enable debug mode
window.enableMonacoDebug = () => {
  localStorage.setItem('monaco-debug', 'true');
  console.log('Monaco debug mode enabled. Autocomplete actions will be logged.');
};

window.disableMonacoDebug = () => {
  localStorage.removeItem('monaco-debug');
  console.log('Monaco debug mode disabled.');
};
